import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import Sidebar from "@/components/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, GanttChartSquare } from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useDateFormat } from '@/utils/date-formatter';
import type { Project } from "@mangelapp/shared";

// Typen für Bauzeitenplan
interface BauzeitEntry {
  id: string;
  projectId: string;
  gewerk: string;
  task: string;
  startDate: string;
  endDate: string;
  status: 'geplant' | 'in_bearbeitung' | 'abgeschlossen' | 'verzögert';
  priority: 'niedrig' | 'mittel' | 'hoch';
  assignedTo?: string;
  notes?: string;
  userId: string;
  createdAt: string;
}

export default function Bauzeitenplan() {
  const { t } = useTranslation(["pages.projectDetails"]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<BauzeitEntry | null>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormat();

  // State für Spaltenbreiten
  const [columnWidths, setColumnWidths] = useState({
    gewerk: 150,
    task: 280,
    startDate: 120,
    endDate: 120,
    duration: 80,
    status: 120,
    priority: 100,
    assignedTo: 180
  });

  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Formulardaten für neuen/bearbeiteten Eintrag
  const [formData, setFormData] = useState({
    gewerk: '',
    task: '',
    startDate: '',
    endDate: '',
    status: 'geplant' as 'geplant' | 'in_bearbeitung' | 'abgeschlossen' | 'verzögert',
    priority: 'mittel' as 'niedrig' | 'mittel' | 'hoch',
    assignedTo: '',
    notes: ''
  });

  // Projekte laden
  const { data: projectsResponse } = useQuery({
    queryKey: ["/api/projects"],
    queryFn: async () => {
      const response = await apiRequest("/api/projects");
      return await response.json();
    },
  });

  const projects = projectsResponse?.data || [];

  // Gewerke laden für Dropdown
  const { data: gewerke = [] } = useQuery({
    queryKey: ["/api/gewerke"],
    select: (data: any[]) => data || []
  });

  // Vorgefertigtes Template mit typischen Bauabläufen
  const templateEntries: BauzeitEntry[] = [
    {
      id: '1',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Rohbau',
      task: 'Fundament und Bodenplatte',
      startDate: format(new Date('2025-02-01'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-02-28'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'hoch',
      assignedTo: 'Bauunternehmen Schmidt',
      notes: 'Witterungsabhängig',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '2',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Rohbau',
      task: 'Erdgeschoss Mauerwerk',
      startDate: format(new Date('2025-03-01'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-03-21'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'hoch',
      assignedTo: 'Bauunternehmen Schmidt',
      notes: 'Nach Fundament',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '3',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Elektro',
      task: 'Rohinstallation',
      startDate: format(new Date('2025-04-01'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-04-25'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'mittel',
      assignedTo: 'Elektro Wagner GmbH',
      notes: 'Parallel zum Rohbau',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '4',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Sanitär',
      task: 'Rohinstallation',
      startDate: format(new Date('2025-04-05'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-04-30'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'mittel',
      assignedTo: 'Heizung & Sanitär Koch',
      notes: 'Nach Elektro-Vorarbeiten',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '5',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Innenausbau',
      task: 'Trockenbau und Dämmung',
      startDate: format(new Date('2025-06-01'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-06-28'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'mittel',
      assignedTo: 'Trockenbau Fischer',
      notes: 'Schallschutz beachten',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
    {
      id: '6',
      projectId: selectedProjectId || 'template',
      userId: 'template',
      gewerk: 'Maler',
      task: 'Grundierung und Spachtel',
      startDate: format(new Date('2025-08-01'), 'yyyy-MM-dd'),
      endDate: format(new Date('2025-08-15'), 'yyyy-MM-dd'),
      status: 'geplant',
      priority: 'niedrig',
      assignedTo: 'Malerbetrieb Weiß',
      notes: 'Vor Bodenbelägen',
      createdAt: format(new Date(), 'yyyy-MM-dd')
    },
  ];

  // Bauzeitenplan-Einträge laden (mit Template als Fallback)
  const { data: bauzeitEntries = templateEntries } = useQuery<BauzeitEntry[]>({
    queryKey: ["/api/bauzeitenplan", selectedProjectId],
    enabled: !!selectedProjectId,
    placeholderData: templateEntries,
  });

  // Template immer anzeigen - sofort sichtbar beim Laden der Seite
  const displayEntries = templateEntries;

  // Drag-Handler für Spaltengrößenänderung
  const handleMouseDown = (column: string, e: React.MouseEvent) => {
    setIsResizing(column);
    setStartX(e.clientX);
    setStartWidth(columnWidths[column as keyof typeof columnWidths]);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;
    const diff = e.clientX - startX;
    const newWidth = Math.max(80, startWidth + diff);
    setColumnWidths(prev => ({
      ...prev,
      [isResizing]: newWidth
    }));
  };

  const handleMouseUp = () => {
    setIsResizing(null);
  };

  // Event Listeners für Drag
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, startX, startWidth]);

  // Mutation für neuen Eintrag
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest(`/api/bauzeitenplan`, {
        method: "POST",
        body: JSON.stringify({
          ...data,
          projectId: selectedProjectId
        }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bauzeitenplan", selectedProjectId] });
      toast({
        title: "Erfolg",
        description: "Bauzeiteneintrag wurde hinzugefügt",
      });
      setShowAddDialog(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Fehler beim Hinzufügen des Eintrags",
        variant: "destructive",
      });
    },
  });

  // Mutation für Update
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest(`/api/bauzeitenplan/${editingEntry?.id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bauzeitenplan", selectedProjectId] });
      toast({
        title: "Erfolg",
        description: "Bauzeiteneintrag wurde aktualisiert",
      });
      setEditingEntry(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Fehler beim Aktualisieren des Eintrags",
        variant: "destructive",
      });
    },
  });

  // Mutation für Löschen
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest(`/api/bauzeitenplan/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bauzeitenplan", selectedProjectId] });
      toast({
        title: "Erfolg",
        description: "Bauzeiteneintrag wurde gelöscht",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Fehler beim Löschen des Eintrags",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      gewerk: '',
      task: '',
      startDate: '',
      endDate: '',
      status: 'geplant',
      priority: 'mittel',
      assignedTo: '',
      notes: ''
    });
  };

  const handleSubmit = () => {
    if (!formData.gewerk || !formData.task || !formData.startDate || !formData.endDate) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus",
        variant: "destructive",
      });
      return;
    }

    if (editingEntry) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const startEdit = (entry: BauzeitEntry) => {
    setEditingEntry(entry);
    setFormData({
      gewerk: entry.gewerk,
      task: entry.task,
      startDate: entry.startDate.split('T')[0],
      endDate: entry.endDate.split('T')[0],
      status: entry.status,
      priority: entry.priority,
      assignedTo: entry.assignedTo || '',
      notes: entry.notes || ''
    });
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 overflow-hidden ml-12 sm:ml-14 md:ml-16 lg:ml-14 xl:ml-16 2xl:ml-20">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-white border-row p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <GanttChartSquare className="h-5 w-5 text-blue-600" />
                <h1 className="text-lg font-medium text-gray-900">Bauzeitenplan</h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="w-48 h-7 text-xs">
                    <SelectValue placeholder="Projekt auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Dialog open={showAddDialog || !!editingEntry} onOpenChange={(open) => {
                  if (!open) {
                    setShowAddDialog(false);
                    setEditingEntry(null);
                    resetForm();
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="h-7 text-xs" onClick={() => setShowAddDialog(true)} disabled={!selectedProjectId}>
                      <Plus className="h-3 w-3 mr-1" />
                      Hinzufügen
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>
                        {editingEntry ? 'Bauzeiteneintrag bearbeiten' : 'Neuer Bauzeiteneintrag'}
                      </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="gewerk" className="text-xs font-medium">Gewerk *</Label>
                        <Select value={formData.gewerk} onValueChange={(value) => setFormData({...formData, gewerk: value})}>
                          <SelectTrigger className="h-7 text-xs">
                            <SelectValue placeholder="Gewerk auswählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {gewerke.map((g: any) => (
                              <SelectItem key={g.id} value={g.name}>{g.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="task" className="text-xs font-medium">Aufgabe *</Label>
                        <Input
                          id="task"
                          value={formData.task}
                          onChange={(e) => setFormData({...formData, task: e.target.value})}
                          placeholder="Beschreibung der Aufgabe"
                          className="h-7 text-xs"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="startDate" className="text-xs font-medium">Startdatum *</Label>
                          <Input
                            id="startDate"
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                            className="h-7 text-xs"
                          />
                        </div>
                        <div>
                          <Label htmlFor="endDate" className="text-xs font-medium">Enddatum *</Label>
                          <Input
                            id="endDate"
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                            className="h-7 text-xs"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="status" className="text-xs font-medium">Status</Label>
                          <Select value={formData.status} onValueChange={(value: any) => setFormData({...formData, status: value})}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="geplant">{t('pages.projectDetails:schedule.status.planned')}</SelectItem>
                              <SelectItem value="in_bearbeitung">{t('pages.projectDetails:schedule.status.inProgress')}</SelectItem>
                              <SelectItem value="abgeschlossen">{t('pages.projectDetails:schedule.status.completed')}</SelectItem>
                              <SelectItem value="verzögert">{t('pages.projectDetails:schedule.status.delayed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="priority" className="text-xs font-medium">Priorität</Label>
                          <Select value={formData.priority} onValueChange={(value: any) => setFormData({...formData, priority: value})}>
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="niedrig">Niedrig</SelectItem>
                              <SelectItem value="mittel">Mittel</SelectItem>
                              <SelectItem value="hoch">Hoch</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="assignedTo" className="text-xs font-medium">Zugewiesen an</Label>
                        <Input
                          id="assignedTo"
                          value={formData.assignedTo}
                          onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                          placeholder="Name oder Firma"
                          className="h-7 text-xs"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="notes" className="text-xs font-medium">Notizen</Label>
                        <Input
                          id="notes"
                          value={formData.notes}
                          onChange={(e) => setFormData({...formData, notes: e.target.value})}
                          placeholder="Zusätzliche Informationen"
                          className="h-7 text-xs"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowAddDialog(false);
                            setEditingEntry(null);
                            resetForm();
                          }}
                          className="h-7 text-xs"
                        >
                          Abbrechen
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSubmit}
                          disabled={createMutation.isPending || updateMutation.isPending}
                          className="h-7 text-xs"
                        >
                          {(createMutation.isPending || updateMutation.isPending) 
                            ? 'Speichern...' 
                            : (editingEntry ? 'Aktualisieren' : 'Erstellen')
                          }
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
          
          {/* Hauptinhalt */}
          <div className="flex-1 overflow-auto p-3">
            {!selectedProjectId ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <GanttChartSquare className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-900 mb-2">Kein Projekt ausgewählt</h3>
                  <p className="text-sm text-gray-600">Bitte wählen Sie ein Projekt aus, um den Bauzeitenplan zu sehen.</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded border">
                {/* Tabellenkopf */}
                <div className="border-b bg-gray-50">
                  <div className="flex">
                    {/* Gewerk Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.gewerk}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Gewerk
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('gewerk', e)}
                      />
                    </div>
                    
                    {/* Aufgabe Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.task}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Aufgabe
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('task', e)}
                      />
                    </div>
                    
                    {/* Startdatum Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.startDate}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Startdatum
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('startDate', e)}
                      />
                    </div>
                    
                    {/* Enddatum Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.endDate}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Enddatum
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('endDate', e)}
                      />
                    </div>
                    
                    {/* Dauer Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.duration}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Dauer
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('duration', e)}
                      />
                    </div>
                    
                    {/* Status Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.status}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Status
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('status', e)}
                      />
                    </div>
                    
                    {/* Priorität Spalte */}
                    <div className="relative border-r" style={{width: columnWidths.priority}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Priorität
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('priority', e)}
                      />
                    </div>
                    
                    {/* Zugewiesen Spalte */}
                    <div className="relative" style={{width: columnWidths.assignedTo}}>
                      <div className="h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                        Zugewiesen an
                      </div>
                      <div 
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 active:bg-blue-400"
                        onMouseDown={(e) => handleMouseDown('assignedTo', e)}
                      />
                    </div>
                    
                    {/* Aktionen */}
                    <div className="w-16 h-7 px-2 flex items-center text-xs font-medium text-gray-700">
                      Aktionen
                    </div>
                  </div>
                </div>
                
                {/* Tabelleninhalt */}
                <div className="divide-y divide-gray-100">
                  {displayEntries.map((entry) => {
                    const duration = differenceInDays(parseISO(entry.endDate), parseISO(entry.startDate)) + 1;
                    return (
                      <div key={entry.id} className="flex hover:bg-gray-50 group">
                        {/* Gewerk */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.gewerk}}>
                          <Badge variant="outline" className="text-xs px-1 py-0 text-blue-600 border-blue-200">
                            {entry.gewerk}
                          </Badge>
                        </div>
                        
                        {/* Aufgabe */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.task}}>
                          <div className="text-xs font-medium text-gray-900 truncate" title={entry.task}>
                            {entry.task}
                          </div>
                        </div>
                        
                        {/* Startdatum */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.startDate}}>
                          <div className="text-xs text-gray-700">
                            {formatDate(entry.startDate)}
                          </div>
                        </div>
                        
                        {/* Enddatum */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.endDate}}>
                          <div className="text-xs text-gray-700">
                            {formatDate(entry.endDate)}
                          </div>
                        </div>
                        
                        {/* Dauer */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.duration}}>
                          <div className="text-xs text-gray-700 text-center w-full">
                            {duration} Tage
                          </div>
                        </div>
                        
                        {/* Status */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.status}}>
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-2 py-0.5 ${
                              entry.status === 'geplant' ? 'text-blue-600 border-blue-200 bg-blue-50' :
                              entry.status === 'in_bearbeitung' ? 'text-yellow-600 border-yellow-200 bg-yellow-50' :
                              entry.status === 'abgeschlossen' ? 'text-green-600 border-green-200 bg-green-50' :
                              'text-red-600 border-red-200 bg-red-50'
                            }`}
                          >
                            {entry.status === 'geplant' ? 'Geplant' :
                             entry.status === 'in_bearbeitung' ? 'In Arbeit' :
                             entry.status === 'abgeschlossen' ? 'Fertig' :
                             'Verzögert'}
                          </Badge>
                        </div>
                        
                        {/* Priorität */}
                        <div className="border-r px-2 py-1 flex items-center" style={{width: columnWidths.priority}}>
                          <Badge 
                            variant="outline" 
                            className={`text-xs px-2 py-0.5 ${
                              entry.priority === 'hoch' ? 'text-red-600 border-red-200 bg-red-50' :
                              entry.priority === 'mittel' ? 'text-yellow-600 border-yellow-200 bg-yellow-50' :
                              'text-green-600 border-green-200 bg-green-50'
                            }`}
                          >
                            {entry.priority === 'hoch' ? 'Hoch' :
                             entry.priority === 'mittel' ? 'Mittel' :
                             'Niedrig'}
                          </Badge>
                        </div>
                        
                        {/* Zugewiesen an */}
                        <div className="px-2 py-1 flex items-center" style={{width: columnWidths.assignedTo}}>
                          <div className="text-xs text-gray-700 truncate" title={entry.assignedTo}>
                            {entry.assignedTo || '-'}
                          </div>
                        </div>
                        
                        {/* Aktionen */}
                        <div className="w-16 px-2 py-1 flex items-center justify-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(entry)}
                            className="h-5 w-5 p-0 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(entry.id)}
                            className="h-5 w-5 p-0 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
